// ============================================================
// TMR Portal Admin — importar uma empresa que já existe no sistema legado
// ============================================================
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn, IconTile } from '../../components/primitives';
import { SearchBox } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { maskCNPJ } from '../../lib/format';
import { useImportCompany, useLegacySearch } from '../../data/companies/queries';
import type { LegacyCompanySnapshot } from '../../data/companies/contract';
import { useDebounced } from '../../useDebounced';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link ImportLegacyModal}. */
interface ImportLegacyModalProps {
  onClose: () => void;
  /** Called with the Empresa's id in tmr_v2, whether it was just imported or was already there. */
  onImported: (companyId: string) => void;
}

/**
 * Brings an Empresa that has existed in the legacy system for years into tmr_v2.
 *
 * Without it the portal only ever lists Empresas born in the new portal, which on a real base is close to
 * nothing (10 B3). The import is idempotent, so running it on a producer already brought over is not an
 * error — it just opens the record that exists.
 */
export function ImportLegacyModal({ onClose, onImported }: ImportLegacyModalProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<LegacyCompanySnapshot | null>(null);

  const debouncedSearch = useDebounced(search);
  const results = useLegacySearch(debouncedSearch);
  const importCompany = useImportCompany();

  const stop = (e: MouseEvent) => e.stopPropagation();
  const importError = importCompany.error instanceof ApiError ? importCompany.error : null;

  const runImport = (producer: LegacyCompanySnapshot) => {
    setSelected(producer);
    importCompany.mutate(producer.legacyProducerId, {
      onSuccess: (imported) => onImported(imported.companyId),
    });
  };

  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: 680, maxWidth: '100%', maxHeight: '82vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '20px 26px', borderBottom: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <div className="pa-eyebrow" style={{ marginBottom: 4 }}>Importar do legado</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, letterSpacing: '-0.01em' }}>Buscar produtor</h3>
              <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--fg-muted)', maxWidth: 460, lineHeight: 1.5 }}>
                Traz para o TMR uma empresa que já existe no sistema legado. Importar duas vezes não duplica
                nada.
              </p>
            </div>
            <button type="button" onClick={onClose} className="pa-icon-btn" style={{ width: 34, height: 34, flexShrink: 0 }}>
              <StrokeIcon d={Icons.x} size={18} />
            </button>
          </div>

          <div style={{ padding: '18px 26px 0' }}>
            <SearchBox value={search} onChange={setSearch} placeholder="Nome ou CNPJ no legado…" width="100%" />
            {importError && (
              <div role="alert" style={{ marginTop: 14, padding: '12px 14px', background: 'oklch(96% 0.03 28)', border: '1px solid oklch(80% 0.09 28)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'oklch(38% 0.12 28)' }}>
                {importError.detail}
                {/* O backend recusa um cadastro legado incompleto nomeando o campo que falta — corrigir é
                    no legado, não aqui. */}
                {importError.status === 400 && (
                  <div style={{ marginTop: 6, fontSize: 12.5 }}>
                    Corrija o cadastro no sistema legado e busque novamente.
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '18px 26px 26px', minHeight: 220 }}>
            <ImportResults
              search={debouncedSearch}
              results={results}
              selectedId={selected?.legacyProducerId ?? null}
              importing={importCompany.isPending}
              onImport={runImport}
            />
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}

/** Props for {@link ImportResults}. */
interface ImportResultsProps {
  search: string;
  results: ReturnType<typeof useLegacySearch>;
  selectedId: number | null;
  importing: boolean;
  onImport: (producer: LegacyCompanySnapshot) => void;
}

/** The search results, plus the states a search can be in. */
function ImportResults({ search, results, selectedId, importing, onImport }: ImportResultsProps) {
  if (search.length <= 2) {
    return <EmptyState message="Busque pelo nome ou CNPJ do produtor." hint="Digite ao menos 3 caracteres." />;
  }
  if (results.isPending) return <Skeleton rows={4} />;
  if (results.isError) return <ErrorState error={results.error} onRetry={() => void results.refetch()} />;
  if (results.data.length === 0) {
    return <EmptyState message="Nenhum produtor encontrado no legado." hint="Revise o termo buscado." />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {results.data.map((producer) => (
        <div
          key={producer.legacyProducerId}
          className="pa-card"
          style={{ padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <IconTile size={36}>
              <StrokeIcon d={Icons.clinic} size={17} />
            </IconTile>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {producer.name || '(sem nome no legado)'}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
                <span style={{ fontFamily: 'var(--font-mono)' }}>
                  {producer.cnpj ? maskCNPJ(producer.cnpj) : 'sem CNPJ'}
                </span>
                {' · '}
                {[producer.addressCity, producer.addressState].filter(Boolean).join('/') || 'sem cidade'}
                {' · '}
                <span style={{ fontFamily: 'var(--font-mono)' }}>#{producer.legacyProducerId}</span>
              </div>
            </div>
          </div>
          <Btn
            variant="secondary"
            onClick={importing ? undefined : () => onImport(producer)}
            disabled={importing}
          >
            {importing && selectedId === producer.legacyProducerId ? 'Importando…' : 'Importar'}
          </Btn>
        </div>
      ))}
    </div>
  );
}
